"use client";

import React, { useState } from "react";
import Image from "next/image";
import SmoothImage from "@/components/SmoothImage";
import { renderTextWithBreaks } from "@/utils/stripHTML";
import { cn } from "@/utils/cn";
import { motion, AnimatePresence } from "framer-motion";
import { useAutoSlider } from "@/hooks/useAutoSlider";
import GalleryModal, {
  MediaItem,
} from "@/components/Layouts/components/GalleryModal";

export interface ApartmentItem {
  id: string;
  tabLabel: string;
  name: string;
  indexNumber?: string;
  indexLabel: string;
  headline: string;
  subDescription: string;
  mainImage: string;
  imageTag: string;
  imageQuote: string;
  floorPlanImage?: string;
  images?: string[];
}

export interface ApartmentSpacesData {
  sectionIndex?: string;
  totalSections?: string;
  tag?: string;
  title?: string;
  description?: string;
  ctaButton?: {
    label: string;
    url?: string;
  };
  apartments?: ApartmentItem[];
}

interface ApartmentSpacesSectionProps {
  data: ApartmentSpacesData;
  onApartmentSelect?: (apartmentId: string) => void;
}

export default function ApartmentSpacesSection({
  data,
  onApartmentSelect,
}: ApartmentSpacesSectionProps) {
  if (!data || !data.apartments || data.apartments.length === 0) return null;

  const { tag = "", title = "", description = "", apartments = [] } = data;

  const [activeApartmentId, setActiveApartmentId] = useState<string>(
    apartments[0]?.id || "",
  );
  const [currentSlideIndex, setCurrentSlideIndex] = useState<number>(0);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMedia, setModalMedia] = useState<MediaItem[]>([]);
  const [modalIndex, setModalIndex] = useState(0);

  const activeApartment =
    apartments.find((a) => a.id === activeApartmentId) || apartments[0];

  const currentImages =
    activeApartment?.images && activeApartment.images.length > 0
      ? activeApartment.images
      : [
          activeApartment?.mainImage ||
            activeApartment?.floorPlanImage ||
            "/images/noi_that_1.png",
        ];

  const safeSlideIndex =
    currentImages.length > 0
      ? Math.abs(currentSlideIndex % currentImages.length)
      : 0;
  const activeSlideImg = currentImages[safeSlideIndex] || currentImages[0];

  const { resetAutoplay, stopAutoplay, startAutoplay } = useAutoSlider({
    interval: 2500,
    enabled: currentImages.length > 1,
    onTick: () => {
      setCurrentSlideIndex((prev) => (prev + 1) % currentImages.length);
    },
  });

  const handleSelectApartment = (apt: ApartmentItem) => {
    setActiveApartmentId(apt.id);
    setCurrentSlideIndex(0);
    resetAutoplay();
    if (onApartmentSelect) {
      onApartmentSelect(apt.id);
    }
  };

  const handleOpenGallery = (startIndex: number = 0) => {
    const mediaList: MediaItem[] = [];

    const imagesToUse =
      activeApartment?.images && activeApartment.images.length > 0
        ? activeApartment.images
        : activeApartment?.mainImage
          ? [activeApartment.mainImage]
          : [];

    imagesToUse.forEach((img, i) => {
      mediaList.push({
        type: "image",
        src: img,
        thumb: img,
        title: `Không gian ${activeApartment.name}${imagesToUse.length > 1 ? ` (${i + 1})` : ""}`,
      });
    });

    if (
      activeApartment?.floorPlanImage &&
      !imagesToUse.includes(activeApartment.floorPlanImage)
    ) {
      mediaList.push({
        type: "image",
        src: activeApartment.floorPlanImage,
        thumb: activeApartment.floorPlanImage,
        title: `Mặt bằng ${activeApartment.name}`,
      });
    }

    if (mediaList.length > 0) {
      setModalMedia(mediaList);
      setModalIndex(Math.min(startIndex, mediaList.length - 1));
      setModalOpen(true);
    }
  };

  return (
    <section
      id="khong-gian-song"
      className="relative w-full min-h-screen lg:h-screen lg:max-h-screen flex flex-col justify-between overflow-hidden bg-[#F6F3ED] text-[#193B2F] select-none px-4 sm:px-8 lg:px-14 xl:px-16 pt-16 sm:pt-20 lg:pt-24 pb-8 sm:pb-10 lg:pb-8"
    >
      {/* BACKGROUND TEXTURE OVERLAY */}
      <div className="absolute inset-0 z-0 pointer-events-none opacity-40 mix-blend-multiply">
        <SmoothImage
          src="/images/background_2.png"
          alt="texture"
          fill
          sizes="100vw"
          priority
          className="object-cover object-center"
        />
      </div>

      <div className="relative z-10 w-full h-full flex flex-col justify-between overflow-hidden gap-2">
        {/* ================= 1. HEADER ROW ================= */}
        <div className="w-full flex flex-col lg:flex-row justify-between items-end gap-3 lg:gap-6 flex-shrink-0">
          {/* LEFT: TAG & MAIN TITLE */}
          <div className="w-full lg:w-[58%] space-y-1.5">
            <div className="flex items-center gap-3">
              <span className="w-7 h-[1px] bg-[#8A724C]" />
              <span className="text-[11px] sm:text-xs font-semibold uppercase tracking-[0.22em] text-[#8A724C] font-sans">
                {tag}
              </span>
            </div>

            <h2
              className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl font-lora font-normal text-[#193B2F] tracking-tight title-leading"
              style={{ lineHeight: 1.15 }}
            >
              {renderTextWithBreaks(title)}
            </h2>
          </div>

          {/* RIGHT: COUNTER & DESCRIPTION */}
          <div className="w-full lg:w-[38%] flex flex-col items-end text-left lg:text-right space-y-1.5 lg:pb-0.5">
            <p className="text-xs font-light font-sans text-[#4A5852] leading-relaxed max-w-md text-justify lg:text-right">
              {renderTextWithBreaks(description)}
            </p>
          </div>
        </div>

        {/* ================= 2. MAIN APARTMENT CARD CONTAINER (2-COLUMN SPLIT) ================= */}
        <div className="w-full flex-1 min-h-0 flex flex-col lg:flex-row items-stretch overflow-hidden border border-[#193B2F]/15 shadow-xl bg-white my-1">
          {/* LEFT COLUMN: MAIN INTERIOR IMAGE SLIDER BANNER WITH OVERLAY QUOTE (~62% WIDTH) */}
          <div
            className="relative w-full lg:w-[62%] h-[280px] sm:h-[340px] md:h-[400px] lg:h-full overflow-hidden group select-none bg-black/20 flex-shrink-0 cursor-pointer"
            onMouseEnter={stopAutoplay}
            onMouseLeave={startAutoplay}
            onClick={() => {
              if (onApartmentSelect) {
                onApartmentSelect(activeApartment.id);
              }
              handleOpenGallery(safeSlideIndex);
            }}
          >
            <AnimatePresence mode="popLayout">
              <motion.div
                key={`${activeApartment.id}-${safeSlideIndex}`}
                initial={{ opacity: 0, scale: 1.02 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.55, ease: "easeInOut" }}
                className="absolute inset-0 w-full h-full"
              >
                <SmoothImage
                  src={activeSlideImg}
                  alt={activeApartment.name}
                  fill
                  sizes="(max-width: 1024px) 100vw, 62vw"
                  priority
                  className="object-cover object-center"
                />
              </motion.div>
            </AnimatePresence>

            {/* Dark Linear Gradient Overlay */}
            <div className="hidden lg:block absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-black/85 via-black/35 to-transparent pointer-events-none z-10" />

            {/* BOTTOM RIGHT DOT NAVIGATION (Shown if currentImages.length > 1) */}
            {currentImages.length > 1 && (
              <div className="absolute bottom-5 right-5 sm:bottom-7 sm:right-7 z-30 flex items-center gap-2 bg-black/40 backdrop-blur-md px-3 py-1.5 rounded-full border border-white/15">
                {currentImages.map((_, idx) => {
                  const isActive = idx === safeSlideIndex;
                  return (
                    <button
                      key={idx}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentSlideIndex(idx);
                        resetAutoplay();
                      }}
                      className={cn(
                        "transition-all duration-300 rounded-full cursor-pointer focus:outline-none",
                        isActive
                          ? "w-6 h-2 bg-white"
                          : "w-2 h-2 bg-white/40 hover:bg-white/70",
                      )}
                      aria-label={`Go to slide ${idx + 1}`}
                    />
                  );
                })}
              </div>
            )}

            {/* Bottom Overlay Content */}
            <div className="absolute inset-x-0 bottom-0 p-5 sm:p-7 md:p-8 pr-28 sm:pr-36 z-20 flex flex-col justify-end text-white pointer-events-none">
              {activeApartment.imageTag && (
                <span className="text-[10px] sm:text-xs font-sans font-bold tracking-[0.2em] text-white/75 uppercase mb-1">
                  {activeApartment.imageTag}
                </span>
              )}
              {activeApartment.imageQuote && (
                <h3 className="font-lora text-2xl sm:text-3xl md:text-4xl font-normal text-white leading-snug">
                  {renderTextWithBreaks(activeApartment.imageQuote)}
                </h3>
              )}
            </div>
          </div>

          {/* RIGHT COLUMN: APARTMENT SPEC, FLOOR PLAN & TABLE SELECTION TABS (~38% WIDTH) */}
          <div className="w-full lg:w-[38%] bg-[#FAF8F5] flex flex-col justify-between overflow-hidden border-t lg:border-t-0 lg:border-l border-[#193B2F]/15 relative">
            {/* TOP CONTENT AREA */}
            <div className="p-5 sm:p-6 md:p-7 flex-1 min-h-0 flex flex-col justify-center overflow-y-auto space-y-4">
              <div className="space-y-2 flex-shrink-0">
                {/* APARTMENT HEADLINE */}
                <h3 className="font-lora text-lg sm:text-xl md:text-2xl font-normal text-[#193B2F] leading-snug">
                  {renderTextWithBreaks(activeApartment.headline)}
                </h3>

                {/* SUB-DESCRIPTION (STRICTLY CLAMPED TO MAX 3 LINES) */}
                <p className="text-xs font-sans font-light text-[#4A5852] leading-relaxed line-clamp-3">
                  {renderTextWithBreaks(activeApartment.subDescription)}
                </p>
              </div>

              {/* FLOOR PLAN BLUEPRINT DRAWING BOX (CENTERED VERTICALLY & CLICK TO OPEN GALLERY MODAL) */}
              <div
                onClick={() => handleOpenGallery(currentImages.length)}
                className="relative w-full my-auto flex-1 min-h-[180px] max-h-[260px] border border-[#193B2F]/15 bg-white p-2.5 flex items-center justify-center overflow-hidden shadow-inner group cursor-pointer"
              >
                <SmoothImage
                  src={activeApartment.floorPlanImage || "/images/ban_ve.png"}
                  alt={`Sơ đồ mặt bằng ${activeApartment.name}`}
                  fill
                  sizes="(max-width: 1024px) 100vw, 38vw"
                  priority
                  className="object-contain p-2 transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            </div>

            {/* BOTTOM FIXED CONTAINER: TABLE SELECTION TABS */}
            <div className="w-full flex-shrink-0">
              {/* TABLE GRID APARTMENT SELECTOR TABS (4 COLUMNS FOR 4 APARTMENTS) */}
              <div className="grid grid-cols-4 w-full border-t border-[#193B2F]/15 text-center select-none bg-[#F7F3EB]">
                {apartments.map((apt) => {
                  const isSelected = activeApartmentId === apt.id;

                  return (
                    <button
                      key={apt.id}
                      type="button"
                      onClick={() => handleSelectApartment(apt)}
                      className={cn(
                        "py-3 px-2 border-r last:border-r-0 border-[#193B2F]/15 flex items-center justify-center gap-1.5 transition-colors cursor-pointer focus:outline-none",
                        isSelected
                          ? "bg-[#E5D4B2] text-[#193B2F] font-bold"
                          : "bg-[#F7F3EB] text-[#193B2F]/70 hover:bg-[#E5D4B2]/40",
                      )}
                    >
                      <span className="text-[9px] sm:text-[10px] font-sans text-[#193B2F]/50 font-normal">
                        {apt.indexNumber || "01"}
                      </span>
                      <span className="font-lora text-xs sm:text-sm text-[#193B2F]">
                        {apt.name}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ================= REUSABLE GALLERY MODAL ================= */}
      <GalleryModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        media={modalMedia}
        initialIndex={modalIndex}
      />
    </section>
  );
}
